import React from "react";
import { NumberField } from "@/components/calc/NumberField";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Users, Receipt, Globe } from "lucide-react";
import { useAppSettings } from "@/context/AppSettings";
import { COUNTRY_PRESETS, employmentCost } from "@/lib/business";
import { fmtCurrency, CURRENCIES } from "@/lib/calc";

const APPLY_KEYS = ["hoursPerWeek", "superPct", "workersCompPct", "leaveLoadingPct", "annualLeaveWeeks", "personalLeaveDays", "publicHolidayDays", "casualLoadingPct", "gstRate", "gstEnabled"];

export default function BusinessSetup() {
  const { business, setBusiness, currency, setCurrency } = useAppSettings();
  const sym = (CURRENCIES[currency] || CURRENCIES.USD).symbol;
  const set = (key, value) => setBusiness((b) => ({ ...b, [key]: value }));
  const isCasual = business.employmentType === "casual";

  const onCountry = (country) => {
    const p = COUNTRY_PRESETS[country];
    setBusiness((b) => {
      const next = { ...b, country };
      if (country !== "CUSTOM") APPLY_KEYS.forEach((k) => { if (p[k] !== undefined) next[k] = p[k]; });
      return next;
    });
    if (p.currency) setCurrency(p.currency);
  };

  const taxLabel = (COUNTRY_PRESETS[business.country] || COUNTRY_PRESETS.CUSTOM).gstLabel;
  const cost = employmentCost(business);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Business Setup</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          These figures are shared across every treatment you price. Set your country, staff wages and
          tax once, and the Treatment Builder uses them automatically.
        </p>
      </div>

      {/* Country */}
      <Card className="rounded-2xl border bg-card p-6 shadow-sm mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-9 w-9 rounded-lg bg-secondary/15 text-secondary flex items-center justify-center">
            <Globe className="h-4.5 w-4.5" style={{ height: 18, width: 18 }} />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-base">Country</h3>
            <p className="text-xs text-muted-foreground">Prefills realistic on-costs & tax — all editable below</p>
          </div>
        </div>
        <Select value={business.country} onValueChange={onCountry}>
          <SelectTrigger className="h-11 rounded-lg max-w-xs" data-testid="country-selector"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(COUNTRY_PRESETS).map(([code, p]) => (
              <SelectItem key={code} value={code} data-testid={`country-option-${code}`}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      <div className="grid md:grid-cols-3 gap-6 items-start">
        {/* Staff wages */}
        <Card className="rounded-2xl border bg-card p-6 shadow-sm md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
              <Users className="h-4.5 w-4.5" style={{ height: 18, width: 18 }} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-base">Default Staff Wage</h3>
              <p className="text-xs text-muted-foreground">Baseline true cost/hr for the cost tools — set each person's real wage, KPI &amp; profit share in Staff</p>
            </div>
          </div>

          {/* Employment type */}
          <div className="inline-flex rounded-lg border border-border p-1 mb-4" data-testid="employment-type-toggle">
            {["permanent", "casual"].map((t) => (
              <button
                key={t}
                onClick={() => set("employmentType", t)}
                data-testid={`employment-${t}`}
                className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                  business.employmentType === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <NumberField label="Base Hourly Rate" prefix={sym} unit="/hr" value={business.baseHourlyRate} onChange={(v) => set("baseHourlyRate", v)} testId="input-base-hourly-rate" hint="The employee's ordinary hourly wage before any on-costs." />
            <NumberField label="Ordinary Hours / Week" unit="hrs" value={business.hoursPerWeek} onChange={(v) => set("hoursPerWeek", v)} testId="input-hours-per-week" hint="Standard full-time hours per week (e.g. 38 in Australia, 40 in NZ/USA)." />
            <NumberField label="Superannuation" unit="%" value={business.superPct} onChange={(v) => set("superPct", v)} testId="input-super-pct" hint="Employer super/retirement contribution, paid on top of wages (AU ~12%)." />
            <NumberField label="Workers Compensation" unit="%" value={business.workersCompPct} onChange={(v) => set("workersCompPct", v)} testId="input-workers-comp-pct" hint="Workers' compensation insurance as a % of wages — use ~2% provisionally." />
          </div>

          {!isCasual ? (
            <>
              <Separator className="my-4" />
              <div className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground mb-3">Paid leave (reduces productive hours)</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <NumberField label="Annual Leave Loading" unit="%" value={business.leaveLoadingPct} onChange={(v) => set("leaveLoadingPct", v)} testId="input-leave-loading-pct" hint="Extra loading paid on the four weeks' annual-leave pay if your award requires it (commonly 17.5%). Applied to the leave weeks only, not the whole year." />
                <NumberField label="Annual Leave" unit="weeks" value={business.annualLeaveWeeks} onChange={(v) => set("annualLeaveWeeks", v)} testId="input-annual-leave-weeks" hint="Paid annual leave per year (AU/NZ = 4 weeks). Time paid but not producing revenue." />
                <NumberField label="Personal / Sick Leave" unit="days" value={business.personalLeaveDays} onChange={(v) => set("personalLeaveDays", v)} testId="input-personal-leave-days" hint="Paid personal/sick leave days per year (AU ~10)." />
                <NumberField label="Public Holidays" unit="days" value={business.publicHolidayDays} onChange={(v) => set("publicHolidayDays", v)} testId="input-public-holiday-days" hint="Paid public holidays per year not worked (~11)." />
              </div>
            </>
          ) : (
            <>
              <Separator className="my-4" />
              <div className="grid sm:grid-cols-2 gap-3">
                <NumberField label="Casual Loading" unit="%" value={business.casualLoadingPct} onChange={(v) => set("casualLoadingPct", v)} testId="input-casual-loading-pct" hint="Loading paid to casuals in lieu of paid leave (AU generally 25%). Casuals aren't paid for leave, so productive hours = paid hours." />
              </div>
            </>
          )}

          <Separator className="my-4" />
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">True hourly cost</span>
              <p className="text-[11px] text-muted-foreground mt-1 max-w-xs">
                {isCasual
                  ? "Base + casual loading + super & workers' comp."
                  : `Wages + on-costs spread over ${cost.productiveWeeks.toFixed(1)} productive weeks (after paid leave).`}
              </p>
            </div>
            <div className="text-right shrink-0">
              <div className="font-heading font-bold text-2xl text-primary tabular-nums" data-testid="true-hourly-cost">
                {fmtCurrency(cost.trueHourly, currency)}
              </div>
              <div className="text-[11px] text-muted-foreground">+{cost.upliftPct.toFixed(1)}% on base wage</div>
            </div>
          </div>
        </Card>

        {/* GST / Tax */}
        <Card className="rounded-2xl border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-9 w-9 rounded-lg bg-accent/20 text-accent flex items-center justify-center">
              <Receipt className="h-4.5 w-4.5" style={{ height: 18, width: 18 }} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-base">Tax / {taxLabel}</h3>
              <p className="text-xs text-muted-foreground">Applied to treatment prices</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <div className="font-medium text-sm">Charge {taxLabel}</div>
                <div className="text-xs text-muted-foreground">Show prices inclusive of {taxLabel.toLowerCase()}</div>
              </div>
              <Switch checked={business.gstEnabled} onCheckedChange={(v) => set("gstEnabled", v)} data-testid="gst-toggle" />
            </div>
            <NumberField label={`${taxLabel} Rate`} unit="%" value={business.gstRate} onChange={(v) => set("gstRate", v)} testId="input-gst-rate" />
          </div>
        </Card>
      </div>
    </div>
  );
}
